<template>
  <section class="app-data-table" :aria-labelledby="showTitle ? titleId : undefined" :aria-label="showTitle ? undefined : title">
    <div class="app-data-table__toolbar">
      <div v-if="showTitle || showCount" class="app-data-table__heading">
        <h2 v-if="showTitle" :id="titleId" class="app-data-table__title">{{ title }}</h2>
        <span v-if="showCount" class="app-data-table__count" aria-label="Количество записей">{{ count }}</span>
      </div>
      <slot name="filters" />
      <div class="app-data-table__utilities" aria-label="Настройки таблицы">
        <q-btn-dropdown flat no-caps icon="density_medium" :label="densityLabel">
          <q-list>
            <q-item v-close-popup clickable @click="setDensity('compact')"><q-item-section>Компактная</q-item-section></q-item>
            <q-item v-close-popup clickable @click="setDensity('comfortable')"><q-item-section>Обычная</q-item-section></q-item>
          </q-list>
        </q-btn-dropdown>
        <q-btn-dropdown v-if="hideableColumns.length" flat no-caps icon="view_column" label="Колонки">
          <q-list class="app-data-table__columns">
            <q-item v-for="column in hideableColumns" :key="column.name" tag="label">
              <q-item-section avatar><q-checkbox :model-value="currentVisibleColumns.includes(column.name)" :aria-label="column.label" @update:model-value="toggleColumn(column.name)" /></q-item-section>
              <q-item-section>{{ column.label }}</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>
      <div v-if="$slots.actions" class="app-data-table__actions"><slot name="actions" /></div>
    </div>

    <app-state
      v-if="state !== 'ready'"
      :state="appState"
      :title="stateTitle"
      :message="stateMessage"
      @reset="$emit('reset')"
      @retry="$emit('retry')"
    />
    <q-table
      v-else
      ref="table"
      v-accessible-scroll-region="{ label: `${title}: таблица`, selector: '.q-table__middle' }"
      v-bind="$attrs"
      flat
      bordered
      row-key="id"
      class="app-data-table__table"
      :dense="currentDensity === 'compact'"
      :rows="rows"
      :columns="columns"
      :pagination="pagination"
      :visible-columns="currentVisibleColumns"
      @request="$emit('request', $event)"
      @update:pagination="$emit('update:pagination', $event)"
    >
      <template #header-selection="scope">
        <q-checkbox v-model="scope.selected" aria-label="Выбрать все строки таблицы" />
      </template>
      <template #body-selection="scope">
        <q-checkbox
          v-model="scope.selected"
          :aria-label="`Выбрать строку ${rowAccessibleName(scope.row)}`"
        />
      </template>
      <template v-for="(_, slotName) in tableSlots" #[slotName]="scope">
        <slot :name="slotName" v-bind="scope" />
      </template>
    </q-table>
  </section>
</template>

<script>
import { getCurrentInstance } from 'vue'
import AppState from './AppState.vue'

const TABLE_STATES = ['ready', 'loading', 'empty', 'filtered-empty', 'error', 'access-denied']

export default {
  name: 'AppDataTable',
  components: { AppState },
  inheritAttrs: false,
  emits: ['reset', 'retry', 'request', 'update:pagination'],
  props: {
    title: { type: String, required: true },
    count: { type: Number, default: 0 },
    rows: { type: Array, default: () => [] },
    columns: { type: Array, required: true },
    pagination: { type: Object, required: true },
    state: { type: String, default: 'ready', validator: (value) => TABLE_STATES.includes(value) },
    stateTitle: { type: String, default: '' },
    stateMessage: { type: String, default: '' },
    storageKey: { type: String, required: true },
    showCount: { type: Boolean, default: true },
    showTitle: { type: Boolean, default: true }
  },
  setup() {
    const instance = getCurrentInstance()
    return { titleId: `app-data-table-${instance.uid}-title` }
  },
  data() {
    const saved = this.readPreference()
    return {
      currentDensity: saved.density || 'compact',
      currentVisibleColumns: this.normalizedVisibleColumns(saved.columns)
    }
  },
  mounted() {
    this.$nextTick(this.annotateInteractiveRows)
  },
  updated() {
    this.$nextTick(this.annotateInteractiveRows)
  },
  computed: {
    appState() {
      return this.state === 'empty' ? 'empty' : this.state
    },
    densityLabel() {
      return this.currentDensity === 'compact' ? 'Компактная' : 'Обычная'
    },
    hideableColumns() {
      return this.columns.filter((column) => column.name !== 'actions' && column.required !== true)
    },
    tableSlots() {
      const excluded = new Set(['actions', 'body-selection', 'filters', 'header-selection'])
      return Object.fromEntries(Object.entries(this.$slots).filter(([name]) => !excluded.has(name)))
    }
  },
  methods: {
    readPreference() {
      try {
        return JSON.parse(localStorage.getItem(`${this.storageKey}:v1`) || '{}')
      } catch {
        return {}
      }
    },
    normalizedVisibleColumns(saved) {
      const available = this.columns.map((column) => column.name)
      const required = this.columns.filter((column) => column.required).map((column) => column.name)
      const selected = Array.isArray(saved) ? saved.filter((name) => available.includes(name)) : available
      return [...new Set([...selected, ...required, 'actions'].filter((name) => available.includes(name)))]
    },
    persistPreference() {
      localStorage.setItem(`${this.storageKey}:v1`, JSON.stringify({
        density: this.currentDensity,
        columns: this.currentVisibleColumns
      }))
    },
    setDensity(value) {
      this.currentDensity = value
      this.persistPreference()
    },
    toggleColumn(name) {
      this.currentVisibleColumns = this.currentVisibleColumns.includes(name)
        ? this.currentVisibleColumns.filter((column) => column !== name)
        : [...this.currentVisibleColumns, name]
      this.persistPreference()
    },
    rowAccessibleName(row) {
      if (!row || typeof row !== 'object') return ''
      return row.id || row.number || row.in_org_number || row.name || ''
    },
    annotateInteractiveRows() {
      if (!this.$attrs.onRowClick) return
      const table = this.$refs.table && this.$refs.table.$el
      if (!table) return
      table.querySelectorAll('tbody tr').forEach((row, index) => {
        row.setAttribute('role', 'button')
        row.setAttribute('tabindex', '0')
        row.setAttribute('aria-label', `Открыть строку: ${this.rowAccessibleName(this.rows[index]) || index + 1}`)
        if (row.dataset.sidKeyboardRow === 'true') return
        row.dataset.sidKeyboardRow = 'true'
        row.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          row.click()
        })
      })
    }
  }
}
</script>

<style scoped>
.app-data-table { width: 100%; max-width: 100%; min-width: 0; }
.app-data-table__toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: var(--sid-space-3); margin-bottom: var(--sid-space-3); }
.app-data-table__heading { display: flex; min-width: 0; flex-wrap: wrap; align-items: center; gap: var(--sid-space-2); }
.app-data-table__title { margin: 0; font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
.app-data-table__count { min-width: 1.75rem; padding: var(--sid-space-1) var(--sid-space-2); border-radius: var(--sid-radius-pill); background: var(--sid-color-surface-subtle); text-align: center; font-variant-numeric: tabular-nums; }
.app-data-table__utilities, .app-data-table__actions { display: flex; min-width: 0; flex-wrap: wrap; align-items: center; gap: var(--sid-space-1); }
.app-data-table__actions { margin-left: auto; }
.app-data-table__columns { min-width: 14rem; }
.app-data-table__table :deep(thead tr th) { position: sticky; z-index: 2; top: 0; background: var(--sid-color-surface-0); font-weight: var(--sid-font-weight-semibold); }
.app-data-table__table :deep(.q-table th), .app-data-table__table :deep(.q-table td) { height: var(--sid-row-compact); }

@media (max-width: 599px) {
  .app-data-table__toolbar { align-items: stretch; flex-direction: column; }
  .app-data-table__utilities, .app-data-table__actions { width: 100%; margin-left: 0; }
  .app-data-table__actions :deep(.q-btn) { width: 100%; min-height: var(--sid-touch-target); }
  .app-data-table__table :deep(.column-priority-secondary) { display: none; }
  .app-data-table__table :deep(tbody tr[role="button"] td) { height: var(--sid-touch-target) !important; }
  .app-data-table__table { max-width: 100%; overflow-x: auto; }
}
</style>
